import { useFormContext } from 'react-hook-form';
import { useModal } from '@ebay/nice-modal-react';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';

import { logger } from '@zep/core-logger';
import { useDialog } from '@zep/dialog';

import {
  useRemovePaymentCard,
  useSubscriptionPlan,
  useUpdateZipCode,
} from '../../../../../services';
import { fDateByCountryCode } from '../../../../../utils';
import { pushGtmPurchaseEvent } from '../../../../../utils/gtmMarketingEvent';
import { paymentFailureFromError } from '../../../payment-tracking/paymentFailure';
import { createPaymentFailureDialog } from '../../../payment-tracking/PaymentFailureContent';
import { isPaymentCancellation } from '../../../payment-tracking/paymentFailureDecision';
import { useGoSignin } from '../../hooks/useGoSignin';
import { findSubscriptionError } from '../../paymentError';
import { capitalizeFirstLetter } from '../../subscriptionUtil';
import { PaymentRetryDialog } from '../modals/PaymentRetryDialog';

import {
  PlanCheckoutFormSchemaType,
  useSubscriptionPaymentContext,
} from './index';

export { findSubscriptionError } from '../../paymentError';

export const usePaymentAction = () => {
  const dialog = useDialog();
  const router = useRouter();
  const moveSignIn = useGoSignin();
  const { t } = useTranslation(['subscription']);

  const { getValues } = useFormContext<PlanCheckoutFormSchemaType>();
  const paymentRetryDialog = useModal(PaymentRetryDialog);

  const { mutateAsync: _mutateSubscriptionPlan } = useSubscriptionPlan();
  const { mutateAsync: removePaymentCard } = useRemovePaymentCard();
  const { mutateAsync: mutateZipCode } = useUpdateZipCode();

  const {
    amplitudeData,
    추가결제금액,
    space,
    selectedMaxCcu,
    selectedPlan,
    selectedPlanInfo,
    isUpdated,
    spaceId,
    spaceHashId,
    updateStatus,
    spacePlan,
    subscriptionPeriod,
    subscriptionRequestHashId,
  } = useSubscriptionPaymentContext();

  const moveCompletePage = (orderId?: string) => {
    const generateUpdateMessage = () => {
      switch (updateStatus.type) {
        case 'DOWN_GRADE': {
          const endDate = spacePlan?.endDate;
          if (!endDate) return updateStatus.messages.at(-1);

          const letter = capitalizeFirstLetter;
          const dateString = fDateByCountryCode(endDate, space.countryCode);
          return selectedMaxCcu < 0
            ? t('subscription:checkout.complete.infiniteUpdateMessage', {
                date: dateString,
                spaceName: space.name,
                plan: letter(selectedPlan),
              })
            : t('subscription:payment.complete.downgradeMessage', {
                date: dateString,
                spaceName: space.name,
                ccu: selectedMaxCcu,
                plan: letter(selectedPlan),
              });
        }
        default: {
          return updateStatus.messages[updateStatus.messages.length - 1]; // 마지막 메세지만
        }
      }
    };

    const { entryMapHashId, thumbnailUrl, hashId } = space;
    const completeTitle = (() => {
      if (
        updateStatus.type === 'UP_GRADE' &&
        추가결제금액 &&
        추가결제금액.value <= 0
      ) {
        return t('subscription:payment.complete.downgradeTitle');
      }
      switch (updateStatus.type) {
        case 'UP_GRADE': {
          return t('subscription:payment.complete.upgradeTitle');
        }
        case 'DOWN_GRADE': {
          return t('subscription:payment.complete.downgradeTitle');
        }
        default: {
          return t('subscription:payment.complete.defaultTitle');
        }
      }
    })();
    const updateMessages = generateUpdateMessage();

    return router.push(
      {
        pathname: '/checkout/complete',
        query: {
          ...router.query,
          orderId,
          thumbnailUrl,
          entryMapHashId,
          title: completeTitle,
          spaceHashId: hashId,
          spaceName: space.name,
          plan: selectedPlan,
          maxCcu: selectedMaxCcu,
          updateStatus: updateStatus.type,
          updateMessages,
          amplitudeData: JSON.stringify(amplitudeData),
          subscriptionPeriod,
        },
      },
      '/checkout/complete',
    );
  };

  const subscribePlan = async (
    _payload: { retry: () => void },
    useRemoveBillingKey = true,
  ) => {
    const { retry } = _payload;

    try {
      // Promise.all 을 사용하려 했으나 결제가 성공해도 우편번호 업데이트가 실패하면 결제 자체를 재시도할 우려가 있어 순차적으로 처리
      if (!(space.countryCode === 'KR' || space.countryCode === 'JP')) {
        await mutateZipCode({
          spaceHashId,

          zipCode: getValues('zipCode') || null,
        });
      }
      const { data } = await _mutateSubscriptionPlan({
        spacePlanTemplateId: selectedPlanInfo.id,
        isUpdated,
        spaceHashId,
        countryCode: space.countryCode,
        subscriptionRequestHashId,
      });

      // 마케팅 전환 추적용 purchase 신호 (GTM Custom Event 트리거로 소비).
      // 사용자가 조작 가능한 URL query가 아니라, 서버가 결제를 확인한 응답
      // (payment.orderId)과 결제 컨텍스트의 실결제액(amplitudeData.price,
      // VAT 포함·업그레이드 시 추가결제금액)을 사용한다.
      // 즉시 결제가 없는 플로우(다운그레이드 등)는 payment가 없어 제외된다.
      if (data.payment?.orderId) {
        // 통화는 결제 대상 플랜의 서버 통화를 우선 사용하고, 신규 구독처럼
        // spacePlan이 없으면 국가 기준으로 결정한다 (괄호로 우선순위 명시).
        const chargedCurrency =
          spacePlan?.currency ?? (space.countryCode === 'KR' ? 'KRW' : 'USD');
        pushGtmPurchaseEvent({
          value: amplitudeData.price,
          currency: chargedCurrency,
          transactionId: data.payment.orderId,
        });
      }

      moveCompletePage(data.payment?.orderId).catch(console.error);
    } catch (e) {
      const title = t('subscription:payment.alertFailPay.title');
      const { type, message } = findSubscriptionError(e);
      const failure = {
        payment_flow: isUpdated
          ? ('plan_change' as const)
          : ('initial_subscription' as const),
        pg_provider:
          space.countryCode === 'KR'
            ? ('toss' as const)
            : space?.countryCode
            ? ('payple' as const)
            : ('unknown' as const),
        space_id: spaceId,
        ...paymentFailureFromError(e),
      };
      const failureDialog = createPaymentFailureDialog(dialog, failure, e);

      switch (type) {
        case '401': {
          await failureDialog.alert({
            title,

            content: t('subscription:payment.alertFailPay.descriptionLogOut'),
            useOutSideClick: false,
          });
          moveSignIn(true).catch(console.error);
          break;
        }
        case 'PG_ERROR': {
          await failureDialog.alert({
            title,
            content: t('subscription:SpaceDetail.changeCardModal.reason', {
              message,
            }),
            useOutSideClick: false,
          });
          break;
        }
        case 'FAIL_TO_EDIT_ZIP_CODE': {
          await dialog.alert({
            title: t('subscription:errorModal.zipCode.title'),
            content: t('subscription:errorModal.zipCode.content'),
          });
          break;
        }
        case 'ZEP_API_ERROR': {
          await paymentRetryDialog.show({
            errorCode: message,
            paymentFailure: isPaymentCancellation(e) ? undefined : failure,
            onRetry: () => {
              retry();
            },
          });
          break;
        }
        case 'UNKNOWN_ERROR': {
          // 분류되지 않은 실패만 수집한다. 401·PG_ERROR(카드 거절)·ZEP_API_ERROR(재시도)·
          // ZIP_CODE 는 정상 비즈니스 결과라 여기서 올리면 노이즈가 된다.
          // ⚠️ 반드시 여기서 잡아야 한다 — 아래에서 throw 하는 Error('SubscriptionPlan')
          // 은 흐름 제어용 신호일 뿐이라 PG 코드·응답·원본 스택이 모두 사라진다.
          logger.captureError(e, {
            tags: { feature: 'subscription-payment', errorType: type },
            extra: { message },
          });
          await failureDialog.alert({
            title,
            content: 'UNKNOWN_ERROR',
            useOutSideClick: false,
          });
        }
      }

      console.error(e);
      /** 신규등록일 경우에만 카드정보를 삭제후 새롭게 요청한다 */
      // 삭제 결과를 기다려 알린다. 호출부는 실제로 지워졌을 때만 카드 등록 상태를 되돌린다.
      const cardRemoved =
        !isUpdated && useRemoveBillingKey
          ? await removePaymentCard(spaceHashId).then(
              () => true,
              (error: unknown) => {
                console.error(error);
                return false;
              },
            )
          : false;

      throw Object.assign(new Error('SubscriptionPlan'), { cardRemoved });
    }
  };

  return {
    subscribePlan,
  };
};
